/**
 * core/intelligence/searchEngine.ts
 *
 * Cross-board search engine.
 *
 * Strategy:
 *   1.  The primary path is the monday.com `items_by_multiple_column_values`
 *       API query — handled via `queryCrossBoardSearch` in @core/api/queries.
 *   2.  For instant, client-side fuzzy matching against already-cached items
 *       (e.g. items loaded by My Work), we expose a local `localSearch` helper
 *       that does a simple multi-field token match.
 *   3.  `rankResults` applies a lightweight TF-style relevance score so that
 *       exact-title matches rise above partial body matches.
 */

import type { MondayItem, SearchResult } from '@core/types';

// ─── Tokeniser ────────────────────────────────────────────────────────────────

function tokenise(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

// ─── Score a single item against a query ──────────────────────────────────────

interface ScoredSearchResult extends SearchResult {
  matchScore: number;
}

function scoreItem(item: MondayItem, queryTokens: string[]): number {
  if (queryTokens.length === 0) return 0;

  const titleTokens = tokenise(item.name ?? '');

  // Build a flat token bag from all column values
  const columnBag = Object.values(item.columnValues ?? {})
    .map((v) => (typeof v === 'string' ? v : ''))
    .join(' ');
  const columnTokens = tokenise(columnBag);

  let score = 0;

  for (const qToken of queryTokens) {
    // Exact match in title → high weight
    if (titleTokens.includes(qToken)) {
      score += 10;
    }
    // Partial match in title
    else if (titleTokens.some((t) => t.startsWith(qToken))) {
      score += 6;
    }

    // Match in column values
    if (columnTokens.includes(qToken)) {
      score += 3;
    } else if (columnTokens.some((t) => t.startsWith(qToken))) {
      score += 1;
    }
  }

  // Bonus for sequential multi-token match (phrase-like)
  if (queryTokens.length > 1) {
    const titleStr = titleTokens.join(' ');
    const queryStr = queryTokens.join(' ');
    if (titleStr.includes(queryStr)) score += 15;
  }

  return score;
}

// ─── Local search ─────────────────────────────────────────────────────────────

/**
 * Filter and rank a pre-loaded list of items against a user query.
 * Returns results in descending relevance order.
 *
 * @param items  - Items already in memory (e.g. from React Query cache)
 * @param query  - Raw search string from the user
 * @param limit  - Maximum results to return (default 20)
 */
export function localSearch(
  items: MondayItem[],
  query: string,
  limit = 20,
): SearchResult[] {
  const tokens = tokenise(query);
  if (tokens.length === 0) return [];

  const scored: ScoredSearchResult[] = items
    .map((item) => ({
      id: item.id,
      type: 'item' as const,
      name: item.name,
      subtitle: item.boardName,
      boardId: item.boardId,
      boardName: item.boardName,
      url: item.url,
      matchScore: scoreItem(item, tokens),
    }))
    .filter((r) => r.matchScore > 0)
    .sort((a, b) => b.matchScore - a.matchScore)
    .slice(0, limit);

  // Strip the internal matchScore before returning
  return scored.map(({ matchScore: _score, ...rest }) => rest);
}

// ─── Rank remote results ──────────────────────────────────────────────────────

/**
 * Re-rank results that came back from the monday.com API.
 * The API already filtered by column values; we apply a lightweight
 * title-first sort on top to improve perceived relevance.
 */
export function rankResults(results: SearchResult[], query: string): SearchResult[] {
  const tokens = tokenise(query);
  if (tokens.length === 0) return results;

  return [...results].sort((a, b) => {
    const scoreA = scoreItem(
      { ...a, id: a.id, name: a.name } as MondayItem,
      tokens,
    );
    const scoreB = scoreItem(
      { ...b, id: b.id, name: b.name } as MondayItem,
      tokens,
    );
    return scoreB - scoreA;
  });
}

// ─── Highlight helper ─────────────────────────────────────────────────────────

/**
 * Wrap matching substrings in `<mark>` tags for display.
 * Safe for use in dangerouslySetInnerHTML after sanitisation if needed,
 * but since both inputs are controlled (our own data), direct use is fine.
 */
export function highlight(text: string, query: string): string {
  if (!query.trim()) return text;
  const tokens = tokenise(query);
  if (tokens.length === 0) return text;

  // Build a single regex alternation, sorted longest-first to avoid partial overlaps
  const pattern = tokens
    .slice()
    .sort((a, b) => b.length - a.length)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');

  try {
    const re = new RegExp(`(${pattern})`, 'gi');
    return text.replace(re, '<mark>$1</mark>');
  } catch {
    return text;
  }
}
